/**
 * AI Preview integration — binds the AIPreview component to the search store
 * (visibility, session, completion capture, Show More -> AI Mode hand-off)
 * inside an error boundary so AI issues never break the results page.
 */

import React from 'react';
import { useSearchStore } from '../search-provider';
import AIPreview from './index';
import AIPreviewErrorBoundary from './error-boundary';
import { AIPreviewState } from './types';

const AIPreviewIntegration: React.FC = () => {
  const {
    searchTerm,
    aiPreviewVisible,
    aiPreviewSessionId,
    aiPreviewState,
    aiPreviewError,
    aiConversational,
    openAIMode,
    completeAIPreview,
  } = useSearchStore();

  const handleError = (error: any) => {
    console.error('AI Preview Error:', error);
    // Error is automatically handled by the search store
  };

  const handleComplete = (response: any) => {
    // Keep the completed overview in the store so the AI Mode tab can seed
    // the conversation even when entered without clicking Show More.
    completeAIPreview(response);
  };

  const handleErrorBoundaryError = (error: any, errorInfo: any) => {
    console.error('AI Preview Error Boundary triggered:', { error, errorInfo });
  };

  return (
    <AIPreviewErrorBoundary
      onError={handleErrorBoundaryError}
      fallback={
        <div
          role="alert"
          aria-live="assertive"
          style={{
            padding: '16px',
            margin: '16px 0',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#6b7280',
          }}
        >
          <div style={{ marginBottom: '8px', fontWeight: 500 }}>
            AI Preview is temporarily unavailable
          </div>
          <div style={{ fontSize: '14px' }}>
            Your search results are still available below.
          </div>
        </div>
      }
    >
      <AIPreview
        searchQuery={searchTerm}
        isVisible={aiPreviewVisible}
        sessionId={aiPreviewSessionId || undefined}
        onError={handleError}
        onComplete={handleComplete}
        // "Show More" hands the conversation off to AI Mode (Google-style).
        // Left off in simple AI mode, where the answer is one-off: the button
        // then expands the full answer in place instead.
        onShowMore={aiConversational ? openAIMode : undefined}
        isLoading={aiPreviewState === AIPreviewState.LOADING}
        mockError={aiPreviewError || undefined}
      />
    </AIPreviewErrorBoundary>
  );
};

export default AIPreviewIntegration;
